// Formularios y Encuestas de Marketing: tipos y componentes compartidos.
import type { JSX, ReactNode } from "react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Copy, X } from "lucide-react";
import { api } from "../../../lib/api.ts";
import { toast } from "../../../components/toast.tsx";

// ────────── Tipos (espejo de apps/api/src/lib/mkt-form-core.ts) ──────────
export type FormKind = "embedded" | "popup" | "page";
export type FieldType = "text" | "email" | "tel" | "textarea" | "select" | "url" | "number" | "date" | "checkbox";
export type BlockKind = "field" | "text" | "image" | "consent" | "hidden" | "separator";
export type HiddenSource = "fixed" | "utm_source" | "utm_medium" | "utm_campaign" | "utm_term" | "utm_content" | "referrer" | "page";

export interface Block {
  id: string; kind: BlockKind; field?: string; label?: string; placeholder?: string; required?: boolean;
  width?: "full" | "half"; text?: string; size?: "sm" | "md" | "lg"; align?: "left" | "center" | "right";
  url?: string; alt?: string; link?: string; name?: string; source?: HiddenSource; value?: string; defaultCountry?: string;
}
export interface FormStyle {
  bg?: string; text?: string; fieldBg?: string; fieldText?: string; fieldBorder?: string; button?: string; buttonText?: string;
  radius?: number; maxWidth?: number; font?: string; bgImage?: string; overlay?: number; overlayColor?: string; padding?: number;
}
export interface FormDesign {
  title?: string; subtitle?: string; buttonLabel?: string; blocks?: Block[]; style?: FormStyle;
  privacy?: { consent?: boolean; text?: string; policyUrl?: string; doubleOptIn?: boolean; confirmSubject?: string };
  analytics?: { utm?: boolean };
  popup?: { trigger?: "delay" | "exit" | "scroll"; delay?: number; scroll?: number; oncePerSession?: boolean };
}
export interface FormSettings {
  tags?: string[]; notifyEmails?: string[]; listIds?: string[];
  assign?: { mode: "default" | "fixed" | "roundrobin"; userId?: string | null; userIds?: string[]; rr?: number };
  autoReply?: { enabled: boolean; subject?: string; html?: string };
  thankYou?: string; redirectUrl?: string; createAccount?: boolean;
}
export interface MktForm {
  id: string; name: string; status: "draft" | "published"; kind: FormKind; publicToken: string; design: FormDesign; settings: FormSettings;
  folder: string | null; impressions: number; conversions: number; contactsCreated: number; accountsCreated: number;
  source: string; createdAt: string; updatedAt: string;
}
export interface FormListRow extends Omit<MktForm, "design" | "settings" | "publicToken"> { creator: string | null }
export interface Links { pageUrl: string; scriptUrl: string; embedCode: string }

export interface FieldOption { key: string; label: string; type: FieldType; group: "contact" | "account" | "custom" | "accountCustom"; options?: string[] }
export interface MktOptions {
  users: Array<{ id: string; name: string }>;
  lists: Array<{ id: string; name: string }>;
  fields: FieldOption[];
  countries: Array<{ iso: string; name: string; dial: string }>;
  folders: string[];
  hasMedia: boolean;
}

export const KIND_LABEL: Record<FormKind, string> = { embedded: "Embebido", popup: "Pop-up", page: "Página" };
export const FONT_LABEL: Record<string, string> = { system: "Sistema", arial: "Arial", georgia: "Georgia", verdana: "Verdana", trebuchet: "Trebuchet", mono: "Monoespaciada" };
export const FONT_STACK: Record<string, string> = {
  system: "system-ui,-apple-system,'Segoe UI',Roboto,Arial,sans-serif", arial: "Arial,Helvetica,sans-serif",
  georgia: "Georgia,'Times New Roman',serif", verdana: "Verdana,Geneva,sans-serif", trebuchet: "'Trebuchet MS',Helvetica,sans-serif",
  mono: "ui-monospace,Menlo,Consolas,monospace",
};
export const DEFAULT_STYLE: Required<Omit<FormStyle, "bgImage">> & { bgImage: string } = {
  bg: "#ffffff", text: "#1f2937", fieldBg: "#ffffff", fieldText: "#111827", fieldBorder: "#d1d5db", button: "#21386d", buttonText: "#ffffff",
  radius: 8, maxWidth: 560, font: "system", bgImage: "", overlay: 0.4, overlayColor: "#000000", padding: 28,
};
export const DEFAULT_POLICY_URL = "https://sacsconsultores.com/politica-de-privacidad/";
export const DEFAULT_CONSENT_TEXT = "Acepto la política de tratamiento de datos personales y autorizo a SACS Consultores a contactarme.";

export const newId = (): string => Math.random().toString(36).slice(2, 10);
export const pct = (a: number, b: number): string => (b > 0 ? `${((a / b) * 100).toFixed(1)}%` : "—");

export function useMktOptions() {
  return useQuery<MktOptions>({ queryKey: ["mkt-form-options"], queryFn: () => api.get("/mkt/forms/options"), staleTime: 60_000 });
}

/** Copia texto al portapapeles con aviso. */
export async function copyText(text: string, what = "Copiado"): Promise<void> {
  try { await navigator.clipboard.writeText(text); toast.success(`${what} al portapapeles.`); }
  catch { toast.error("No se pudo copiar. Selecciona el texto y cópialo manualmente."); }
}

export function CopyBox({ value, label, mono = true }: { value: string; label?: string; mono?: boolean }): JSX.Element {
  const [done, setDone] = useState(false);
  return (
    <div className="min-w-0">
      {label && <div className="label">{label}</div>}
      <div className="flex items-stretch rounded-md border border-line bg-canvas overflow-hidden">
        <pre className={`flex-1 min-w-0 m-0 px-3 py-2.5 text-xs leading-relaxed text-ink-secondary whitespace-pre-wrap break-all select-all ${mono ? "font-mono" : ""}`}>{value}</pre>
        <button type="button" className={`shrink-0 flex items-center gap-1.5 px-3 text-xs font-medium border-l border-line transition-colors hover:bg-surface-hover ${done ? "text-success" : "text-ink-secondary"}`}
          onClick={() => { void copyText(value); setDone(true); setTimeout(() => setDone(false), 1500); }}>
          {done ? <Check size={14} /> : <Copy size={14} />} {done ? "Copiado" : "Copiar"}
        </button>
      </div>
    </div>
  );
}

export function Metric({ label, value, hint, onClick, active }: { label: string; value: ReactNode; hint?: string; onClick?: () => void; active?: boolean }): JSX.Element {
  const cls = `card p-4 text-left w-full ${onClick ? "hover:border-primary cursor-pointer" : ""} ${active ? "ring-2 ring-primary" : ""}`;
  const body = (<><div className="dt-label">{label}</div><div className="text-2xl font-semibold text-ink tnum mt-1">{value}</div>{hint && <div className="text-xs text-ink-muted mt-0.5">{hint}</div>}</>);
  return onClick ? <button type="button" className={cls} onClick={onClick}>{body}</button> : <div className={cls}>{body}</div>;
}

/** Entrada de etiquetas/correos tipo "chips". */
export function ChipsInput({ values, onChange, placeholder, validate, max = 20, maxLen = 30 }: {
  values: string[]; onChange: (v: string[]) => void; placeholder?: string; validate?: (v: string) => string | null; max?: number; maxLen?: number;
}): JSX.Element {
  const [draft, setDraft] = useState("");
  const add = (): void => {
    const v = draft.trim().slice(0, maxLen);
    if (!v) return;
    const err = validate?.(v);
    if (err) { toast.error(err); return; }
    if (!values.includes(v) && values.length < max) onChange([...values, v]);
    setDraft("");
  };
  return (
    <div className="input flex flex-wrap gap-1.5 items-center min-h-[38px] py-1.5">
      {values.map((v) => (
        <span key={v} className="badge bg-primary-soft text-primary gap-1">
          {v}<button type="button" aria-label={`Quitar ${v}`} onClick={() => onChange(values.filter((x) => x !== v))}><X size={12} /></button>
        </span>
      ))}
      <input className="flex-1 min-w-[120px] bg-transparent outline-none text-sm" value={draft} placeholder={values.length ? "" : placeholder}
        onChange={(e) => setDraft(e.target.value)} onBlur={add}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } else if (e.key === "Backspace" && !draft && values.length) onChange(values.slice(0, -1)); }} />
    </div>
  );
}

/** Lista de casillas para elegir varios elementos (listas, usuarios). */
export function CheckList({ items, selected, onChange, empty }: { items: Array<{ id: string; name: string }>; selected: string[]; onChange: (v: string[]) => void; empty: string }): JSX.Element {
  if (!items.length) return <p className="text-xs text-ink-muted">{empty}</p>;
  return (
    <div className="max-h-44 overflow-y-auto scrollbar-thin border border-line rounded-md divide-y divide-line">
      {items.map((it) => (
        <label key={it.id} className="flex items-center gap-2 px-3 py-1.5 text-sm text-ink-secondary cursor-pointer hover:bg-surface-hover">
          <input type="checkbox" checked={selected.includes(it.id)}
            onChange={(e) => onChange(e.target.checked ? [...selected, it.id] : selected.filter((x) => x !== it.id))} />
          <span className="truncate">{it.name}</span>
        </label>
      ))}
    </div>
  );
}

export function Toggle({ checked, onChange, label, hint, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string; disabled?: boolean }): JSX.Element {
  return (
    <label className={`flex items-start gap-3 ${disabled ? "opacity-60" : "cursor-pointer"}`}>
      <button type="button" role="switch" aria-checked={checked} disabled={disabled} onClick={() => onChange(!checked)}
        className={`mt-0.5 relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${checked ? "bg-primary" : "bg-line-strong"}`}>
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
      </button>
      <span><span className="text-sm text-ink">{label}</span>{hint && <span className="block text-xs text-ink-muted">{hint}</span>}</span>
    </label>
  );
}

export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: Array<{ id: T; label: string }>; value: T; onChange: (v: T) => void }): JSX.Element {
  return (
    <div className="flex gap-1 border-b border-line overflow-x-auto scrollbar-thin">
      {tabs.map((t) => (
        <button key={t.id} type="button" onClick={() => onChange(t.id)}
          className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 -mb-px ${value === t.id ? "border-primary text-primary font-medium" : "border-transparent text-ink-secondary hover:text-ink"}`}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

/** Carpeta: lista lateral con "Todas" + carpetas existentes. */
export function FolderNav({ folders, value, onChange }: { folders: string[]; value: string; onChange: (v: string) => void }): JSX.Element | null {
  if (!folders.length) return null;
  return (
    <div className="card p-2 md:w-52 shrink-0 h-fit">
      <div className="dt-label px-2 py-1">Carpetas</div>
      {["", ...folders].map((f) => (
        <button key={f || "_all"} type="button" onClick={() => onChange(f)}
          className={`w-full px-2 py-1.5 rounded-md text-sm text-left truncate ${value === f ? "bg-primary-soft text-primary font-medium" : "text-ink-secondary hover:bg-surface-hover"}`}>
          {f || "Todas"}
        </button>
      ))}
    </div>
  );
}

/** Descarga un CSV autenticado (cookie de sesión) como archivo. */
export async function downloadCsv(path: string, filename: string): Promise<void> {
  try {
    const res = await fetch(`/api/v1${path}`, { credentials: "include" });
    if (!res.ok) throw new Error(String(res.status));
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  } catch { toast.error("No se pudo exportar el archivo."); }
}
